import {
  YES_NO,
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G6. Wniosek o wydanie decyzji zezwalającej na wyłączenie gruntów z produkcji rolniczej — art. 11 ust. 1 i art. 12
 * ustawy z 3.02.1995 r. o ochronie gruntów rolnych i leśnych (zwolnienie z należności na cele mieszkaniowe: art. 12a).
 * Brak wzoru ogólnopolskiego; układ jak wzór Starostwa Powiatowego w Wyszkowie
 * (https://bip.powiat-wyszkowski.pl/pliki/2023/karty_uslug/gg/Wniosek_o_wylaczenie_gruntow_z_produkcji_rolnej.pdf,
 * pobrany 22.10.2026): wnioskodawca, rodzaj inwestycji, adres nieruchomości i tabela działek (nr, powierzchnia do
 * wyłączenia, klasa), załączniki. Pochodzenie gleby i pytanie o zwolnienie mieszkaniowe — według „Uwag dla EBI” katalogu.
 * Tytuł prawny i wypis z rejestru gruntów — praktyka urzędów (nieobowiązkowe, do potwierdzenia z wydziałem).
 */
const HOUSING: EbiCondition = { field: "rodzajInwestycji", equals: "mieszkaniowa" };
const EXEMPT: EbiCondition = { all: [HOUSING, { field: "limitMieszkaniowy", equals: "tak" }] };
const PLAN: EbiCondition = { field: "przeznaczenieTerenu", equals: "mpzp" };
const ZONING: EbiCondition = { field: "przeznaczenieTerenu", equals: "wz" };

const G6: IEbiFormDefinition = {
  code: "G6",
  version: 1,
  title: "Wniosek o wydanie decyzji zezwalającej na wyłączenie gruntów z produkcji rolniczej",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.County,
  description: "Zezwolenie na wyłączenie gruntów rolnych z produkcji (tzw. odrolnienie) pod budowę lub inną inwestycję nierolniczą.",
  keywords: ["wyłączenie z produkcji", "odrolnienie", "grunty rolne", "klasa gruntu", "budowa domu", "ochrona gruntów"],
  organ: "Starosta",
  legalBasis: "art. 11 ust. 1 ustawy z dnia 3 lutego 1995 r. o ochronie gruntów rolnych i leśnych",
  request:
    "Na podstawie art. 11 ust. 1 ustawy z dnia 3 lutego 1995 r. o ochronie gruntów rolnych i leśnych wnoszę o wydanie decyzji zezwalającej na wyłączenie z produkcji rolniczej gruntów wskazanych we wniosku.",
  feeLabel: "wg stawek",
  fee: "Wniosek i decyzja bez opłaty skarbowej; pełnomocnictwo — 17 zł. Po wyłączeniu gruntu: należność jednorazowa i opłaty roczne (art. 12), z wyjątkiem zwolnień z art. 12a.",
  deadlineLabel: "do miesiąca",
  deadline: "Do miesiąca, w sprawie szczególnie skomplikowanej do 2 miesięcy (art. 35 KPA).",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "inwestycja",
      title: "Rodzaj inwestycji",
      fields: [
        {
          code: "rodzajInwestycji",
          type: "radio",
          label: "Wyłączenie jest związane z",
          required: true,
          options: [
            { value: "zagrodowa", label: "budową obiektów w zabudowie zagrodowej wyłącznie o charakterze rolniczym" },
            {
              value: "art12b",
              label: "zamiarem wykonywania w zabudowie zagrodowej działalności innej niż działalność rolnicza (art. 12b ustawy)",
            },
            { value: "mieszkaniowa", label: "budownictwem mieszkaniowym (budynek jednorodzinny albo wielorodzinny)" },
            { value: "inna", label: "inną inwestycją" },
          ],
        },
        {
          code: "opisInwestycji",
          type: "textarea",
          label: "Opis inwestycji",
          required: true,
          maxLength: 3000,
          placeholder: "np. budowa budynku mieszkalnego jednorodzinnego z garażem i drogą dojazdową",
        },
        {
          code: "limitMieszkaniowy",
          type: "radio",
          label:
            "Czy powierzchnia wyłączana na cele budownictwa mieszkaniowego nie przekracza 0,05 ha (budynek jednorodzinny) albo 0,02 ha na każdy lokal mieszkalny (budynek wielorodzinny)?",
          hint: "W tych granicach nie pobiera się należności i opłat rocznych (art. 12a ustawy).",
          required: true,
          visibleWhen: HOUSING,
          options: YES_NO,
        },
      ],
    },
    {
      code: "grunty",
      title: "Grunty do wyłączenia",
      description:
        "Decyzja jest wymagana dla użytków rolnych klas I–III oraz klas IV–VI wytworzonych z gleb pochodzenia organicznego (art. 11 ust. 1 ustawy) i wydaje się ją przed pozwoleniem na budowę (art. 11 ust. 4).",
      fields: [
        { code: "gmina", type: "text", label: "Gmina", required: true, width: "half", maxLength: 100 },
        { code: "miejscowoscGruntow", type: "text", label: "Miejscowość", required: true, width: "half", maxLength: 100 },
        { code: "ulicaGruntow", type: "text", label: "Ulica (jeśli jest)", maxLength: 200 },
        {
          code: "dzialki",
          type: "repeat",
          label: "Działki i powierzchnia do wyłączenia",
          itemLabel: "Działka",
          required: true,
          min: 1,
          max: 30,
          fields: [
            { code: "obreb", type: "text", label: "Obręb ewidencyjny", required: true, width: "half", maxLength: 100 },
            { code: "numer", type: "text", label: "Numer działki", required: true, width: "half", maxLength: 50, placeholder: "np. 125/4" },
            { code: "powierzchnia", type: "number", label: "Powierzchnia do wyłączenia [ha]", required: true, width: "third", decimals: 4, min: 0.0001, max: 9999 },
            { code: "klasa", type: "text", label: "Użytek i klasa gruntu", required: true, width: "third", maxLength: 20, placeholder: "np. RIIIa, ŁIV" },
            {
              code: "gleba",
              type: "select",
              label: "Pochodzenie gleby",
              required: true,
              width: "third",
              options: [
                { value: "mineralna", label: "mineralne" },
                { value: "organiczna", label: "organiczne" },
              ],
            },
            { code: "kw", type: "text", label: "Numer księgi wieczystej (jeśli założona)", maxLength: 30, placeholder: "np. LU1I/00012345/6" },
          ],
        },
        {
          code: "przeznaczenieTerenu",
          type: "radio",
          label: "Przeznaczenie terenu na cele nierolnicze wynika z",
          required: true,
          options: [
            { value: "mpzp", label: "miejscowego planu zagospodarowania przestrzennego" },
            { value: "wz", label: "decyzji o warunkach zabudowy (brak planu miejscowego)" },
          ],
        },
        {
          code: "wspolwlasnosc",
          type: "radio",
          label: "Czy działka jest współwłasnością kilku osób, a wniosek składa tylko jedna z nich?",
          required: true,
          options: YES_NO,
        },
      ],
    },
    {
      code: "oplata",
      title: "Należność i opłaty",
      fields: [
        feeInfo(
          "infoOplata",
          "Wniosek i decyzja nie podlegają opłacie skarbowej. Po faktycznym wyłączeniu gruntu należy uiścić należność jednorazową (art. 12 ust. 7 — według klasy i rodzaju użytku, pomniejszoną o wartość rynkową gruntu, art. 12 ust. 6) oraz opłaty roczne. Należności i opłat nie pobiera się w granicach zwolnień z art. 12a (budownictwo mieszkaniowe).",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "projektZagospodarowania",
      label: "Projekt zagospodarowania działki lub terenu z zestawieniem powierzchni przeznaczonej do wyłączenia",
      hint: "Zgodnie z projektem budowlanym, z uwzględnieniem klas i użytków w granicach działki (działek).",
      required: true,
    },
    { code: "wypisMpzp", label: "Wypis i wyrys z miejscowego planu zagospodarowania przestrzennego", visibleWhen: PLAN, required: true },
    { code: "decyzjaWz", label: "Decyzja o warunkach zabudowy (ostateczna)", visibleWhen: ZONING, required: true },
    {
      code: "wartoscGruntu",
      label: "Dokument określający wartość rynkową gruntu (operat szacunkowy, umowa kupna albo oświadczenie o wartości)",
      hint: "Należność pomniejsza się o wartość gruntu (art. 12 ust. 6 ustawy).",
      visibleWhen: { not: EXEMPT },
      required: true,
    },
    {
      code: "zgodaWspolwlascicieli",
      label: "Zgoda współwłaścicieli działki",
      visibleWhen: { field: "wspolwlasnosc", equals: "tak" },
      required: true,
    },
    {
      code: "tytulPrawny",
      label: "Dokument potwierdzający tytuł prawny do nieruchomości",
      hint: "Praktyka urzędów — np. odpis z księgi wieczystej, akt notarialny, umowa; wystarczy numer księgi wieczystej w liście działek, jeśli urząd go akceptuje.",
    },
    {
      code: "wypisRejestru",
      label: "Wypis z rejestru gruntów i kopia mapy ewidencyjnej",
      hint: "Praktyka urzędów — nieobowiązkowo.",
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default G6;
